/**
 * SubscriptionPlans — the 3-tier subscription selector for the Plan & Coins tab.
 *
 * Renders the config-driven tiers from useSubscriptionPlans (Starter / Practice /
 * Family) as cards. The dynamic fields (name, sessionsPerMonth, priceSgd,
 * recommended) come from the backend (GET /system-config/subscription-plans, CCM-04);
 * the static marketing copy (description / suitable-for / bullets / CTA label) is the
 * council-signed-off content held here, keyed by planKey (PLAN_COPY).
 *
 * Behaviour:
 *   - loading / error (with retry) / empty states, mirroring the top-up packs section.
 *   - Each card CTA calls onChoose(planKey); the parent runs the Stripe checkout.
 *   - The plan flagged `recommended` gets the highlighted treatment + warm CTA.
 *
 * See Requirements/PRD-Subscription-Tiers.md §7. Until the backend plans endpoint +
 * plan-key checkout land, this section naturally shows loading/error (no broken CTAs).
 *
 * CCM-03: rendered only inside PlanAndCoinsTab (ParentGuard-protected).
 */

import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import type { SubscriptionPlan } from '@/types/billing';
import { cn } from '@/lib/utils';

// ─── Static, council-signed-off copy (keyed by planKey) ─────────────────────────
// Launch-pricing sprint (2026-07-16, Amber/Isabelle sign-off): the app paywall now
// mirrors the marketing site's conversion hierarchy — hours-first value anchor,
// struck-through usual price, "keep your launch price while subscribed" lock line,
// and a "not answer credits" clarifier. Dynamic fields
// (name/sessions/priceSgd/usualPriceSgd/recommended) come from the API; only the
// hours anchor and CTA label live here as static copy.
interface PlanCopy {
  /** Hours-first value anchor, e.g. "Up to 10 hours of guided PSLE English practice each month". */
  hoursLine: string;
  /** "Best for" one-liner (mirrors the marketing website card). */
  bestFor: string;
  bullets: string[];
  cta: string;
}

/** Shared clarifier under every card (Amber/Isabelle sign-off 2026-07-16). */
const LAUNCH_CLARIFIER = 'Not answer credits. Guided practice time.';

// 2026-07-16: card copy is 1:1 with the marketing website pricing page (PR #86) so
// the in-app paywall and the public site read identically (Wayne directive).
const PLAN_COPY: Record<string, PlanCopy> = {
  starter: {
    hoursLine: 'Up to 10 hours of guided PSLE English practice each month',
    bestFor: 'Weekly guided practice between school, tuition, and homework.',
    bullets: [
      'Guided comprehension practice with hints before answers',
      'Step-by-step reasoning before your child commits',
      'Practice history you can review after every passage',
    ],
    cta: 'Lock in Starter',
  },
  practice: {
    hoursLine: 'Up to 20 hours of guided PSLE English practice each month',
    bestFor: 'Regular PSLE English practice across the school week.',
    bullets: [
      'More room for consistent comprehension practice',
      'Helps your child check the passage before answering',
      'Builds practice momentum in the run-up to tests and PSLE',
    ],
    cta: 'Lock in Practice',
  },
  // Family is retired from public selection (backend visible=false) and normally
  // never renders here; kept only as a safe fallback should an env still expose it.
  // Public/display name stays "Family" — never "Unlimited" or "Family Plan".
  family: {
    hoursLine: 'Guided practice for siblings or more frequent practice',
    bestFor: 'Siblings sharing a household plan, or more frequent practice before PSLE.',
    bullets: ['The same guided hints and parent-reviewable history every session'],
    cta: 'Choose Family',
  },
};

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface SubscriptionPlansProps {
  /** Called with the chosen planKey when a plan CTA is clicked (parent runs checkout). */
  onChoose: (planKey: string) => void;
  /** The planKey whose checkout is currently in-flight (shows a spinner + disables all). */
  pendingPlan: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function sessionsLabel(plan: SubscriptionPlan): string {
  if (plan.unlimited || plan.sessionsPerMonth === null) {
    return 'Unlimited guided sessions included';
  }
  return `${plan.sessionsPerMonth} guided sessions included`;
}

// ─── PlanCard ───────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: SubscriptionPlan;
  isPending: boolean;
  anyPending: boolean;
  onChoose: (planKey: string) => void;
}

function PlanCard({ plan, isPending, anyPending, onChoose }: PlanCardProps) {
  const copy = PLAN_COPY[plan.planKey];
  const ctaLabel = copy?.cta ?? `Choose ${plan.name}`;
  const launchPrice = plan.priceSgd.toFixed(2);
  // Only show the struck-through anchor when there is a genuine, higher usual price.
  const usualPrice =
    typeof plan.usualPriceSgd === 'number' && plan.usualPriceSgd > plan.priceSgd
      ? plan.usualPriceSgd.toFixed(2)
      : null;

  return (
    <Card
      className={cn(
        'relative flex flex-1 flex-col',
        plan.recommended && 'border-2 border-otter-orange-500 shadow-md md:-translate-y-2'
      )}
    >
      {plan.recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-otter-orange-500 text-white">★ Recommended</Badge>
        </div>
      )}
      <CardContent className="flex flex-1 flex-col p-5 pt-6">
        <p className="font-display text-xl font-semibold text-otter-earth-800">{plan.name}</p>

        {/* Hours-first value anchor (leads the card — Amber/Isabelle sign-off) */}
        {copy && (
          <p className="mt-1 text-sm font-bold text-otter-orange-600">{copy.hoursLine}</p>
        )}

        {/* Price block: struck-through usual price above the launch price */}
        <div className="mt-3">
          {usualPrice && (
            <p className="text-xs font-medium text-gray-400">
              Usual <span className="line-through">S${usualPrice}</span> / month
            </p>
          )}
          <p className="font-display text-2xl font-bold text-otter-earth-800">
            <span className="mr-1 align-middle text-xs font-semibold uppercase tracking-wide text-gray-500">
              Launch price:
            </span>
            <span className="align-super text-sm">S$</span>
            {launchPrice}
            <span className="ml-1 text-xs font-semibold text-gray-500">/ month</span>
          </p>
          {/* Launch-pricing pill + keep line — two elements, matching the website card
              (PR #86): the "locked in" pill carries the 🔒, the keep line states the
              always-true condition. Pill shows only when the plan is on launch pricing. */}
          {usualPrice && (
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-otter-orange-100 px-2 py-0.5 text-[11px] font-semibold text-otter-orange-700">
              🔒 Launch price locked in
            </span>
          )}
          <p className="mt-1 text-xs font-semibold text-otter-earth-700">
            Keep S${launchPrice}/month while subscribed.
          </p>
        </div>

        {/* Sessions included (secondary to the hours anchor) */}
        <p className="mt-3 text-sm font-medium text-gray-700">{sessionsLabel(plan)}</p>

        {/* "Best for" + bullets — 1:1 with the marketing website card (PR #86) */}
        {copy && (
          <>
            <p className="mt-3 text-xs text-gray-500">
              <span className="mb-0.5 block font-semibold uppercase tracking-wide text-otter-earth-700">
                Best for
              </span>
              {copy.bestFor}
            </p>
            <ul className="mt-3 mb-1 space-y-2">
              {copy.bullets.map((b) => (
                <li key={b} className="flex gap-2 text-sm text-gray-700">
                  <span aria-hidden="true" className="mt-0.5 text-green-600">
                    ✓
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </>
        )}

        <Button
          variant={plan.recommended ? 'warm' : 'outline'}
          className="mt-auto h-11 w-full rounded-[10px]"
          onClick={() => onChoose(plan.planKey)}
          disabled={anyPending}
          aria-label={`${ctaLabel} plan`}
        >
          {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {ctaLabel}
        </Button>
        {/* Clarifier under the CTA — blocks the "cheap AI answer credits" comparison
            (matches the website card order) */}
        <p className="mt-2 text-center text-xs text-gray-500">{LAUNCH_CLARIFIER}</p>
        <p className="mt-1 text-center text-xs text-gray-400">🔒 Secure checkout via Stripe</p>
      </CardContent>
    </Card>
  );
}

// ─── SubscriptionPlans (main export) ────────────────────────────────────────────

export function SubscriptionPlans({ onChoose, pendingPlan }: SubscriptionPlansProps) {
  const { plans, isLoading, isError, refetch } = useSubscriptionPlans();

  if (isLoading) {
    return <LoadingState message="Loading plans..." />;
  }

  if (isError) {
    return (
      <ErrorState
        title="Could not load plans"
        message="Please try again."
        onRetry={refetch}
      />
    );
  }

  if (plans.length === 0) {
    return <EmptyState message="No plans available at the moment." />;
  }

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
      {plans.map((plan) => (
        <PlanCard
          key={plan.planKey}
          plan={plan}
          isPending={pendingPlan === plan.planKey}
          anyPending={pendingPlan !== null}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}
